import { WebWidgetTypingIndicator, WebWidgetTypingParticipantType } from '../types/webWidget';

const PARTICIPANT_TYPE_BY_NUMBER: Record<number, WebWidgetTypingParticipantType> = {
  0: 'companyMember',
  2: 'aiAssistant',
};

const normalizeParticipantType = (value: unknown): WebWidgetTypingParticipantType | null => {
  if (typeof value === 'number' && value in PARTICIPANT_TYPE_BY_NUMBER) {
    return PARTICIPANT_TYPE_BY_NUMBER[value];
  }

  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === '0' || normalized === 'companymember') {
    return 'companyMember';
  }

  if (normalized === '2' || normalized === 'aiassistant') {
    return 'aiAssistant';
  }

  return null;
};

export const normalizeTypingIndicators = (value: unknown): WebWidgetTypingIndicator[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap(item => {
    if (!item || typeof item !== 'object') {
      return [];
    }

    const indicator = item as Record<string, unknown>;
    const participantType = normalizeParticipantType(indicator.participantType);
    if (!participantType) {
      return [];
    }

    const expiresAtUtc = typeof indicator.expiresAtUtc === 'string' ? indicator.expiresAtUtc.trim() : '';
    if (!expiresAtUtc) {
      return [];
    }

    const participantId = typeof indicator.participantId === 'string' && indicator.participantId.trim()
      ? indicator.participantId.trim()
      : undefined;

    const displayName = typeof indicator.displayName === 'string' && indicator.displayName.trim()
      ? indicator.displayName.trim()
      : undefined;

    return [{
      participantType,
      participantId,
      displayName,
      expiresAtUtc,
    }];
  });
};

export const filterActiveTypingIndicators = (
  indicators: WebWidgetTypingIndicator[],
  nowMs: number = Date.now(),
): WebWidgetTypingIndicator[] => {
  return indicators.filter(indicator => {
    const expiresAtMs = new Date(indicator.expiresAtUtc).getTime();
    return Number.isFinite(expiresAtMs) && expiresAtMs > nowMs;
  });
};

export const pruneTypingIndicatorsAfterMessages = (
  indicators: WebWidgetTypingIndicator[],
  messageParticipantTypes: WebWidgetTypingParticipantType[],
): WebWidgetTypingIndicator[] => {
  if (messageParticipantTypes.length === 0) {
    return indicators;
  }

  const completedTypes = new Set(messageParticipantTypes);
  return indicators.filter(indicator => !completedTypes.has(indicator.participantType));
};

export const hasActiveTypingIndicators = (indicators: WebWidgetTypingIndicator[]): boolean =>
  filterActiveTypingIndicators(indicators).length > 0;
